/**
 * AlertConfig Container Component
 *
 * Main container for alert configuration management.
 * Manages threshold editing, webhook config, alert history display,
 * and scope-level configuration (global/provider/pool).
 */

import React, { useState, useCallback, useEffect } from 'react';
import { clsx } from 'clsx';
import { ThresholdEditor, type ThresholdValues } from './ThresholdEditor';
import { WebhookConfig, type WebhookConfigValues } from './WebhookConfig';
import { AlertHistory } from './AlertHistory';
import { StatusBadge } from '../shared/StatusBadge';
import {
  llmAdminApi,
  type AlertConfigItem,
} from '../../../services/llmAdminApi';

export interface AlertConfigProps {
  token?: string;
  disabled?: boolean;
  className?: string;
}

type ConfigScope = 'global' | 'provider' | 'pool';

interface EditingConfig {
  scope: ConfigScope;
  warning_threshold: number;
  critical_threshold: number;
  notification_type: string;
  webhook_url: string;
  webhook_headers: Record<string, string>;
  cooldown_minutes: number;
  enabled: boolean;
  provider_name: string;
  pool_index: string;
}

const defaultConfig: EditingConfig = {
  scope: 'global',
  warning_threshold: 0.80,
  critical_threshold: 0.95,
  notification_type: 'log',
  webhook_url: '',
  webhook_headers: {},
  cooldown_minutes: 30,
  enabled: true,
  provider_name: '',
  pool_index: '',
};

export const AlertConfig: React.FC<AlertConfigProps> = ({
  token,
  disabled = false,
  className = '',
}) => {
  const [configs, setConfigs] = useState<AlertConfigItem[]>([]);
  const [editing, setEditing] = useState<EditingConfig>(defaultConfig);
  const [selectedScope, setSelectedScope] = useState<ConfigScope>('global');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await llmAdminApi.getAlertConfigs(token);
      setConfigs(result);

      const globalConfig = result.find((c) => c.scope === 'global');
      if (globalConfig) {
        setEditing({
          scope: 'global',
          warning_threshold: globalConfig.warning_threshold,
          critical_threshold: globalConfig.critical_threshold,
          notification_type: globalConfig.notification_type,
          webhook_url: globalConfig.webhook_url || '',
          webhook_headers: {},
          cooldown_minutes: globalConfig.cooldown_minutes,
          enabled: globalConfig.enabled,
          provider_name: '',
          pool_index: '',
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载告警配置失败');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleScopeChange = useCallback(
    (scope: ConfigScope) => {
      setSelectedScope(scope);

      const matchingConfig = configs.find((c) => {
        if (scope === 'global') return c.scope === 'global';
        if (scope === 'provider') return c.scope === 'provider';
        return c.scope === 'pool';
      });

      if (matchingConfig) {
        setEditing({
          scope,
          warning_threshold: matchingConfig.warning_threshold,
          critical_threshold: matchingConfig.critical_threshold,
          notification_type: matchingConfig.notification_type,
          webhook_url: matchingConfig.webhook_url || '',
          webhook_headers: {},
          cooldown_minutes: matchingConfig.cooldown_minutes,
          enabled: matchingConfig.enabled,
          provider_name: matchingConfig.provider_name || '',
          pool_index: matchingConfig.pool_index?.toString() || '',
        });
      } else {
        const globalConfig = configs.find((c) => c.scope === 'global');
        setEditing({
          ...defaultConfig,
          scope,
          warning_threshold: globalConfig?.warning_threshold ?? 0.80,
          critical_threshold: globalConfig?.critical_threshold ?? 0.95,
        });
      }
    },
    [configs]
  );

  const handleThresholdChange = useCallback((value: ThresholdValues) => {
    setEditing((prev) => ({
      ...prev,
      warning_threshold: value.warning_threshold,
      critical_threshold: value.critical_threshold,
    }));
  }, []);

  const handleWebhookChange = useCallback((value: WebhookConfigValues) => {
    setEditing((prev) => ({
      ...prev,
      notification_type: value.notification_type,
      webhook_url: value.webhook_url,
      webhook_headers: value.webhook_headers,
      cooldown_minutes: value.cooldown_minutes,
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (editing.warning_threshold >= editing.critical_threshold) {
      setError('警告阈值必须小于严重阈值');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await llmAdminApi.updateAlertConfig(token, {
        scope: editing.scope,
        warning_threshold: editing.warning_threshold,
        critical_threshold: editing.critical_threshold,
        notification_type: editing.notification_type,
        webhook_url: editing.webhook_url || null,
        webhook_headers:
          Object.keys(editing.webhook_headers).length > 0
            ? editing.webhook_headers
            : null,
        cooldown_minutes: editing.cooldown_minutes,
        enabled: editing.enabled,
        provider_name: editing.scope !== 'global' ? editing.provider_name : undefined,
        pool_index:
          editing.scope === 'pool' && editing.pool_index
            ? parseInt(editing.pool_index, 10)
            : undefined,
      });

      setSuccess('告警配置保存成功');
      setTimeout(() => setSuccess(null), 3000);
      await fetchConfigs();
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }, [token, editing, fetchConfigs]);

  if (loading) {
    return (
      <div className={clsx('p-8 text-center text-gray-500', className)}>
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
        加载告警配置...
      </div>
    );
  }

  return (
    <div className={clsx('space-y-6', className)}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-800">告警配置</h3>
        <StatusBadge
          variant={configs.length > 0 ? 'success' : 'neutral'}
          size="sm"
          dot
        >
          {configs.length} 项配置
        </StatusBadge>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-500 hover:text-red-700"
          >
            关闭
          </button>
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            配置作用域
          </h4>
          <div className="flex gap-2">
            {(['global', 'provider', 'pool'] as ConfigScope[]).map((scope) => (
              <button
                key={scope}
                type="button"
                onClick={() => handleScopeChange(scope)}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  selectedScope === scope
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                {scope === 'global' ? '全局' : scope === 'provider' ? '提供商' : '池'}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4 space-y-6">
          {selectedScope !== 'global' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  提供商名称
                </label>
                <input
                  type="text"
                  value={editing.provider_name}
                  onChange={(e) =>
                    setEditing((prev) => ({ ...prev, provider_name: e.target.value }))
                  }
                  disabled={disabled}
                  placeholder="例如: openai"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                />
              </div>
              {selectedScope === 'pool' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    池索引
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={editing.pool_index}
                    onChange={(e) =>
                      setEditing((prev) => ({ ...prev, pool_index: e.target.value }))
                    }
                    disabled={disabled}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
                  />
                </div>
              )}
            </div>
          )}

          <ThresholdEditor
            value={{
              warning_threshold: editing.warning_threshold,
              critical_threshold: editing.critical_threshold,
            }}
            onChange={handleThresholdChange}
            disabled={disabled}
          />

          <WebhookConfig
            value={{
              webhook_url: editing.webhook_url,
              webhook_headers: editing.webhook_headers,
              notification_type: editing.notification_type,
              cooldown_minutes: editing.cooldown_minutes,
            }}
            onChange={handleWebhookChange}
            disabled={disabled}
          />

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={editing.enabled}
                onChange={(e) =>
                  setEditing((prev) => ({ ...prev, enabled: e.target.checked }))
                }
                disabled={disabled}
                className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">启用此配置</span>
            </label>
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleSave}
              disabled={disabled || saving}
              className={clsx(
                'px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium',
                'hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {saving ? '保存中...' : '保存配置'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <AlertHistory token={token} />
      </div>
    </div>
  );
};

export default AlertConfig;
